import { type ComputedRef, Ref, computed, ref, watch } from "@nuxtjs/composition-api";

import { useDebounce } from "@/composables/utils";
import { useCreatorProductStore } from "@/store/creator-products";
import { SEARCH_INPUT_DEBOUNCE_DELAY } from "@/utils/constants";

type useCreatorShopProductsReturn = {
  searchTerm: Ref<string>;
  showLoading: ComputedRef<boolean>;
  showError: ComputedRef<boolean>;
  productIds: ComputedRef<Array<string>>;
  onPaginate: () => void;
};

export const useCreatorShopRecentProducts = (profileId: string): useCreatorShopProductsReturn => {
  if (!profileId) {
    throw new Error("useCreatorShopRecentProducts() requires a profileId be supplied.");
  }

  const _store = useCreatorProductStore();
  const _isTyping = ref(false);
  const _recentFeedIds = ref<Array<string>>(_store.recentProductsFeed[profileId]?.ids ?? []);
  const _recentFeedSearchIds = ref<Array<string>>([]);

  const searchTerm = ref("");

  const showLoading = computed(() =>
    Boolean(
      _isTyping.value || _store.recentProductsFetchState.pending || _store.recentProductsSearchFetchState.pending,
    ),
  );

  const showError = computed(() =>
    Boolean(_store.recentProductsFetchState.error || _store.recentProductsSearchFetchState.error),
  );

  const productIds = computed(() => (searchTerm.value ? _recentFeedSearchIds.value : _recentFeedIds.value));

  const onPaginate = async () => {
    if (searchTerm.value) {
      await _store.searchCreatorRecentProducts(profileId, searchTerm.value);
      _recentFeedSearchIds.value = _store.recentProductsSearchFeed[searchTerm.value]?.ids ?? [];
    } else {
      await _store.fetchCreatorRecentProducts(profileId);
      _recentFeedIds.value = _store.recentProductsFeed[profileId]?.ids ?? [];
    }
  };

  const _fetchSearch = async (profileId: string, term: string) => {
    _isTyping.value = false; // let fetch state take over the loading indicator
    await _store.searchCreatorRecentProducts(profileId, term);
    _recentFeedSearchIds.value = _store.recentProductsSearchFeed[term]?.ids ?? [];
  };

  const _debouncedSearch = useDebounce(_fetchSearch, SEARCH_INPUT_DEBOUNCE_DELAY);

  watch(
    () => searchTerm.value,
    (value: string) => {
      _debouncedSearch.cancel(); // cancel any previous call as the user is still interacting
      _store.clearRecentProductsSearchFeed(); // clear previous searches, as we don't index them by profile just term

      if (value) {
        _isTyping.value = true; // show the loading indicator even though a fetch is technically not occurring
        _debouncedSearch(profileId, value);
      } else {
        _isTyping.value = false; // the user cleared or deleted the contest of search
      }
    },
  );

  return {
    searchTerm,
    showLoading,
    showError,
    productIds,
    onPaginate,
  };
};
